import React, {
   forwardRef,
   useEffect,
   useImperativeHandle,
   useRef,
   useState,
} from 'react';

(function polyfillGetUserMedia() {
   if (typeof window === 'undefined') {
      return;
   }

   if (navigator.mediaDevices === undefined) {
      (navigator as any).mediaDevices = {};
   }

   if (navigator.mediaDevices.getUserMedia === undefined) {
      navigator.mediaDevices.getUserMedia = function (constraints) {
         const getUserMedia =
            navigator.mediaDevices.getUserMedia ||
            navigator.webkitGetUserMedia ||
            navigator.mozGetUserMedia ||
            navigator.msGetUserMedia;
         if (!getUserMedia) {
            return Promise.reject(
               new Error('getUserMedia is not implemented in this browser')
            );
         }

         return new Promise(function (resolve, reject) {
            getUserMedia.call(navigator, constraints);
         });
      };
   }
})();

function hasGetUserMedia() {
   return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

export type WebcamProps = Omit<React.HTMLProps<HTMLVideoElement>, 'ref'> & {
   audio?: boolean;
   audioConstraints?: MediaStreamConstraints['audio'];
   disablePictureInPicture?: boolean;
   onUserMedia?: (stream: MediaStream) => void;
   onUserMediaError?: (error: string | DOMException) => void;
   screenshotFormat?: 'image/webp' | 'image/png' | 'image/jpeg';
   screenshotQuality?: number;
   videoConstraints?: MediaStreamConstraints['video'];
   closeImageModalWithError?: Function;
   setHasPermission?: Function;
};

export interface ChildComponentHandles {
   getScreenshot?: () => undefined | string | null;
   getMediaStream?: () => MediaStream | null | HTMLVideoElement;
}

export const ImageWebcam = forwardRef<ChildComponentHandles, WebcamProps>(
   (props, ref) => {
      const {
         audio = false,
         disablePictureInPicture = false,
         onUserMedia = () => undefined,
         onUserMediaError = () => undefined,
         screenshotFormat = 'image/webp',
         screenshotQuality = 0.92,
         audioConstraints,
         videoConstraints = {
            video: { facingMode: 'user' ,
               width: { ideal: 1920 }, // Request 1920px width
               height: { ideal: 1080 }, // Request 1080px height
            },
         },
         style = {},
         children,
         setHasPermission,
         closeImageModalWithError,
         ...rest
      } = props;
      const localRef = useRef<HTMLVideoElement | null>(null);
      const [hasUserMedia, setHasUserMedia] = useState<boolean | null>(null);
      const canvas = useRef<HTMLCanvasElement>();
      const videoStream = useRef<MediaStream | null>(null);

      useEffect(() => {
         if (!hasGetUserMedia()) {
            onUserMediaError('getUserMedia not supported');
            return;
         }
         if (!hasUserMedia) {
            requestUserMedia();
         }
         return () => {
            stopMediaStream();
         };
      }, []);

      useImperativeHandle(ref, () => ({
         getScreenshot: () => {
            if (!hasUserMedia) return null;
            const screenshotCanvas = getCanvas();
            return screenshotCanvas?.current?.toDataURL(
               screenshotFormat,
               screenshotQuality
            );
         },
         getMediaStream: () => {
            return localRef.current;
         },
      }));

      const stopMediaStream = () => {
         const stream = videoStream.current;
         if (stream) {
            stream.getVideoTracks().forEach((track) => {
               stream.removeTrack(track);
               track.stop();
            });
            stream.getAudioTracks().forEach((track) => {
               stream.removeTrack(track);
               track.stop();
            });
         }
      };

      const handleUserMedia = (err: Error | null) => {
         if (err || !videoStream.current) {
            setHasUserMedia(false);
            onUserMediaError(err?.message || 'Failed to get user media');
            closeImageModalWithError?.();
            return;
         }

         try {
            if (localRef.current) {
               localRef.current.srcObject = videoStream.current;
            }
            setHasUserMedia(true);
         } catch (error) {
            closeImageModalWithError?.();
         }

         onUserMedia(videoStream.current);
      };

      const requestUserMedia = async () => {
         if ('MediaRecorder' in window) {
            try {
               videoStream.current = await navigator?.mediaDevices?.getUserMedia(
                  videoConstraints as MediaStreamConstraints
               );
               handleUserMedia(null);
            } catch (error) {
               handleUserMedia(error as Error);
            }
         } else {
            closeImageModalWithError?.();
         }
      };

      const getCanvas = () => {
         if (!localRef.current) return null;

         if (!canvas.current) {
            let canvasWidth = localRef.current.videoWidth;
            let canvasHeight = localRef.current.videoHeight;

            if (canvasWidth === 0 || canvasHeight === 0) return;

            const aspectRatio = canvasWidth / canvasHeight;
            canvasHeight = window.innerHeight;
            canvasWidth = canvasHeight * aspectRatio;
            canvas.current = document.createElement('canvas');
            let context = canvas?.current?.getContext('2d');

            if (context && canvas.current) {
               canvas.current.width = canvasWidth;
               canvas.current.height = canvasHeight;

               context.translate(canvas.current.width, 0);
               context.scale(-1, 1);

               context.imageSmoothingEnabled = false;
               context.drawImage(
                  localRef.current,
                  0,
                  0,
                  canvas.current.width,
                  canvas.current.height
               );
            }
         }

         return canvas;
      };

      useEffect(() => {
         setHasPermission?.(hasUserMedia);
      }, [hasUserMedia]);

      return (
         <video
            autoPlay
            disablePictureInPicture={disablePictureInPicture}
            muted={!audio}
            playsInline
            ref={localRef}
            style={{ ...style, transform: `${style.transform || ''} scaleX(-1)` }}
            height="100vh"
            {...rest}
         />
      );
   }
);
